import { useState, type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";

import { FormControlWrapper } from "components/Shared";
import { TextBox, MultiSelect } from "components/Shared/Inputs";
import { ReportSectionAccordion } from "components/CriticalIncident/components/Shared/ReportSectionAccordion";
import { ChooseYesNoOther } from "components/CriticalIncident/components/ChooseYesNoOther";
import { ACTIONS_TAKEN_MAX_LENGTH, NOTIFIED_PARTY_LABEL_KEYS, makeEmptyNotifiedParty } from "../../RiskAssessments.constants";
import { useRiskAssessmentFieldActionsContext } from "../../hooks/RiskAssessmentFieldActionsContext";
import { NotifiedPartyFields } from "../NotifiedPartyFields";

export const ActionsTakenSection: FunctionComponent = () => {
  const { t } = useTranslation("criticalIncident");
  const {
    basePath,
    record,
    recordTouched,
    recordErrors,
    isReadonly,
    notifiedPartyOptions,
    handleParticipantValueChange,
    makeParticipantChangeHandler,
    handleNotifiedPartyTypesChange,
  } = useRiskAssessmentFieldActionsContext();
  const [expandedNotifiedPartyIndex, setExpandedNotifiedPartyIndex] = useState<number | false>(false);
  const [isNotifiedPartySelectOpen, setIsNotifiedPartySelectOpen] = useState(false);

  const toggleNotifiedParty = (partyIndex: number) => (isExpanded: boolean) => {
    setExpandedNotifiedPartyIndex(isExpanded ? partyIndex : false);
  };

  const isDutyToReportDescriptionRequired = record.hasDutyToReport === "Y" || record.hasDutyToReport === "O";
  const dutyToReportDescriptionLabel = t("duty_to_report_description");
  const notifiedPartiesDetailsLabel = t("notified_parties_details");

  return (
    <>
      <Grid item sm={12}>
        <FormControlWrapper
          isReadonly={isReadonly}
          type="radio.yesnoother"
          label={t("is_there_a_duty_to_report")}
          name={`${basePath}.hasDutyToReport`}
          error={Boolean(recordTouched?.hasDutyToReport) && Boolean(recordErrors?.hasDutyToReport)}
          helperText={recordTouched?.hasDutyToReport ? t(recordErrors?.hasDutyToReport || " ") : ""}
        >
          <ChooseYesNoOther
            label={t("is_there_a_duty_to_report")}
            name={`${basePath}.hasDutyToReport`}
            value={record.hasDutyToReport}
            onChange={handleParticipantValueChange("hasDutyToReport")}
            error={Boolean(recordTouched?.hasDutyToReport) && Boolean(recordErrors?.hasDutyToReport)}
            helperText={recordTouched?.hasDutyToReport ? t(recordErrors?.hasDutyToReport || " ") : ""}
          />
        </FormControlWrapper>
      </Grid>

      <Grid item sm={12}>
        <FormControlWrapper
          isReadonly={isReadonly}
          label={dutyToReportDescriptionLabel}
          name={`${basePath}.dutyToReportDescription`}
          fullWidth
        >
          <TextBox
            multiline
            minRows={4}
            maxRows={6}
            label={dutyToReportDescriptionLabel}
            required={isDutyToReportDescriptionRequired}
            name={`${basePath}.dutyToReportDescription`}
            value={record.dutyToReportDescription ?? ""}
            onChange={makeParticipantChangeHandler("dutyToReportDescription")}
            slotProps={{ input: { maxLength: ACTIONS_TAKEN_MAX_LENGTH } }}
            error={Boolean(recordTouched?.dutyToReportDescription) && Boolean(recordErrors?.dutyToReportDescription)}
            helperText={recordTouched?.dutyToReportDescription ? t(recordErrors?.dutyToReportDescription || " ") : ""}
          />
        </FormControlWrapper>
      </Grid>

      <Grid item sm={12}>
        <FormControlWrapper
          isReadonly={isReadonly}
          label={notifiedPartiesDetailsLabel}
          name={`${basePath}.notifiedPartiesDetails`}
          fullWidth
        >
          <TextBox
            multiline
            minRows={4}
            maxRows={6}
            label={notifiedPartiesDetailsLabel}
            required={isDutyToReportDescriptionRequired}
            name={`${basePath}.notifiedPartiesDetails`}
            value={record.notifiedPartiesDetails ?? ""}
            onChange={makeParticipantChangeHandler("notifiedPartiesDetails")}
            slotProps={{ input: { maxLength: ACTIONS_TAKEN_MAX_LENGTH } }}
            error={Boolean(recordTouched?.notifiedPartiesDetails) && Boolean(recordErrors?.notifiedPartiesDetails)}
            helperText={recordTouched?.notifiedPartiesDetails ? t(recordErrors?.notifiedPartiesDetails || " ") : ""}
          />
        </FormControlWrapper>
      </Grid>

      <Grid item sm={12}>
        <FormControlWrapper
          isReadonly={isReadonly}
          label={t("measures_taken_to_reduce_risk")}
          name={`${basePath}.measureTakenToReduceRisks`}
          fullWidth
        >
          <TextBox
            multiline
            minRows={4}
            maxRows={6}
            label={t("measures_taken_to_reduce_risk")}
            name={`${basePath}.measureTakenToReduceRisks`}
            value={record.measureTakenToReduceRisks ?? ""}
            onChange={makeParticipantChangeHandler("measureTakenToReduceRisks")}
            slotProps={{ input: { maxLength: ACTIONS_TAKEN_MAX_LENGTH } }}
            error={Boolean(recordTouched?.measureTakenToReduceRisks) && Boolean(recordErrors?.measureTakenToReduceRisks)}
            helperText={recordTouched?.measureTakenToReduceRisks ? t(recordErrors?.measureTakenToReduceRisks || " ") : ""}
          />
        </FormControlWrapper>
      </Grid>

      <Grid item sm={12}>
        <FormControlWrapper
          isReadonly={isReadonly}
          type="radio.yesnoother"
          label={t("agreement_against_suicide")}
          name={`${basePath}.agreementAgainstSuicide`}
          error={Boolean(recordTouched?.agreementAgainstSuicide) && Boolean(recordErrors?.agreementAgainstSuicide)}
          helperText={recordTouched?.agreementAgainstSuicide ? t(recordErrors?.agreementAgainstSuicide || " ") : ""}
        >
          <ChooseYesNoOther
            label={t("agreement_against_suicide")}
            name={`${basePath}.agreementAgainstSuicide`}
            value={record.agreementAgainstSuicide}
            onChange={handleParticipantValueChange("agreementAgainstSuicide")}
            error={Boolean(recordTouched?.agreementAgainstSuicide) && Boolean(recordErrors?.agreementAgainstSuicide)}
            helperText={recordTouched?.agreementAgainstSuicide ? t(recordErrors?.agreementAgainstSuicide || " ") : ""}
          />
        </FormControlWrapper>
      </Grid>

      {['Y', 'O'].includes(record.agreementAgainstSuicide) && (
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t("agreement_against_suicide_description")}
            name={`${basePath}.agreementAgainstSuicideDescription`}
            fullWidth
            required
          >
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t("agreement_against_suicide_description")}
              name={`${basePath}.agreementAgainstSuicideDescription`}
              value={record.agreementAgainstSuicideDescription ?? ""}
              onChange={makeParticipantChangeHandler("agreementAgainstSuicideDescription")}
              slotProps={{ input: { maxLength: ACTIONS_TAKEN_MAX_LENGTH } }}
              error={Boolean(recordTouched?.agreementAgainstSuicideDescription) && Boolean(recordErrors?.agreementAgainstSuicideDescription)}
              helperText={recordTouched?.agreementAgainstSuicideDescription ? t(recordErrors?.agreementAgainstSuicideDescription || " ") : ""}
            />
          </FormControlWrapper>
        </Grid>
      )}

      <Grid item sm={12}>
        <FormControlWrapper
          isReadonly={isReadonly}
          type="multiselect"
          label={t("who_did_you_inform")}
          name={`${basePath}.selectedNotifiedParties`}
          fullWidth
          options={notifiedPartyOptions}
        >
          <MultiSelect
            label={t("who_did_you_inform")}
            name={`${basePath}.selectedNotifiedParties`}
            options={notifiedPartyOptions}
            value={record.selectedNotifiedParties}
            onChange={async (event) => {
              const shouldClose = await handleNotifiedPartyTypesChange()(event);
              if (shouldClose) setIsNotifiedPartySelectOpen(false);
            }}
            open={isNotifiedPartySelectOpen}
            onOpen={() => setIsNotifiedPartySelectOpen(true)}
            onClose={() => setIsNotifiedPartySelectOpen(false)}
          />
        </FormControlWrapper>
      </Grid>

      {(record.partiesNotified ?? [])
        .filter((party) => party?.partyType !== "none")
        .map((party) => {
          const partyIndex = (record.partiesNotified ?? []).indexOf(party);

          return (
            <Grid item sm={12} key={party?.partyType}>
              <ReportSectionAccordion
                label={t(NOTIFIED_PARTY_LABEL_KEYS[party?.partyType])}
                name={`${basePath}.partiesNotified[${partyIndex}]`}
                pristineValue={makeEmptyNotifiedParty(party?.partyType)}
                removeHidden
                expanded={expandedNotifiedPartyIndex === partyIndex}
                onToggle={toggleNotifiedParty(partyIndex)}
                readonly={isReadonly}
              >
                <NotifiedPartyFields partyIndex={partyIndex} />
              </ReportSectionAccordion>
            </Grid>
          );
        })}
    </>
  );
};
